// ambient.js — the ambient intelligence loop (docs/comprehend-live-ambient.md).
// Owns the mic while the panel is up: cuts ~15s VAD-aligned chunks and makes
// ONE backend call each (/v1/synapse/ambient/chunk). The server transcribes
// (Whisper prompt-conditioned on the rolling window tail), runs the brain,
// commits settled transcript to live visits IN-REQUEST, and returns semantic
// a gate {act, instruction} — when raised, this runtime hands the
// instruction to the ONE action agent (onGate) that the wake word also
// uses. Ambient never takes actions itself (no second palette → no drift;
// see the decision of record in docs/comprehend-live-ambient.md).
//
// The FE is the renderer/executor of UI effects only; it never gatekeeps the
// chart. State (window/lastSpeaker) is FE-carried so the lambda is stateless.
import { liveStore } from './liveStore';
import { Emitter } from './events';
import { looksLikeMediaContainer } from './audioChunk';

export const MIN_CHUNK_MS = 10000; // normal-cadence floor (continuous speech)
export const HARD_CUT_MS = 20000; // latest cut, mid-utterance if it must
export const SILENCE_MS = 600; // inter-sentence pause that cuts after the floor
// Fast path for a short utterance followed by a CLEAR stop — e.g. a command
// ("dismiss that") spoken into a quiet moment. Cuts at ~UTTERANCE_MIN_MS
// instead of waiting for the 10s floor, so worst-case command latency is
// ~4s + round trip, not ~10s+. A longer silence (1.2s) keeps brief
// inter-sentence breaths during continuous speech from triggering it.
const UTTERANCE_MIN_MS = 3500;
const UTTERANCE_SILENCE_MS = 1200;
export const RMS_SPEECH = 0.012; // analyser RMS above this counts as voice
const VAD_TICK_MS = 150;
// A chunk needs this much CUMULATIVE voiced time to be worth a Whisper
// call — a single 150ms blip in a quiet room yields prompt hallucinations
// ("I'm going to go to the next video"), not speech.
const MIN_VOICED_MS = 750;
const WINDOW_CAP = 4000; // chars of provisional transcript carried

// TODO(demo cap): blunt safety limits so a forgotten panel doesn't burn
// Whisper minutes at an empty desk. Replace before real rollout with
// something smarter (schedule awareness / presence detection / wake-word
// re-arm). Collapsing and reopening the panel resumes listening.
const MAX_SESSION_MS = 60 * 60 * 1000; // 1 hour, demo ceiling
const MAX_IDLE_CHUNKS = 20; // consecutive silent chunks ≈ nobody's there

// Internal-only X-ray for the Brain Monitor (🧠 dev chip): a ring buffer of
// chunk lifecycle events plus a live snapshot the monitor polls. Negligible
// overhead when the monitor is closed (plain object writes, no listeners).
const INSPECTOR_CAP = 60;
let inspectorEventId = 0;
export const ambientInspector = {
  emitter: new Emitter(),
  // newest first: {id, at, type: 'chunk'|'skipped'|'suppressed'|'autostop', ...}
  events: [],
  live: null, // {seq, startedAt, lastVoiceAt, rms, heardVoice} | null
  window: '', // current provisional water line
  lastSpeaker: null,
  push(type, payload) {
    inspectorEventId += 1;
    this.events.unshift({
      id: inspectorEventId,
      at: Date.now(),
      type,
      ...payload,
    });
    if (this.events.length > INSPECTOR_CAP) this.events.length = INSPECTOR_CAP;
    this.emitter.emit('update');
  },
  reset() {
    this.live = null;
    this.emitter.emit('update');
  },
};

export class AmbientRuntime {
  constructor({ apiClient, onGate, onStatus, onCatchup }) {
    this.apiClient = apiClient;
    // onGate(instruction): the brain raised its hand — hand this plain-
    // language instruction to the one action agent.
    this.onGate = onGate || (() => {});
    this.onStatus = onStatus || (() => {});
    // onCatchup(active): a forced (⚡ / wake-word) flush is in flight, from
    // the cut until its round-trip resolves — drives the bolt animation.
    this.onCatchup = onCatchup || (() => {});
    this.stream = null;
    this.recorder = null;
    this.audioCtx = null;
    this.analyser = null;
    this.vadTimer = null;
    this.parts = [];
    this.chunkStartedAt = 0;
    this.lastVoiceAt = 0;
    this.heardVoice = false;
    this.pendingForced = false;
    // When set, the next flushed chunk is transcribed/committed but the brain's
    // gate action is NOT executed — used for the "Hey Comprehend" flush so the
    // ambient brain doesn't double-act on words the realtime agent is handling.
    this.pendingNoAct = false;
    // The "Hey Comprehend" flush cuts the pre-wake window BEFORE the chat
    // overlay opens, but `onstop` fires a task later — by then the store
    // subscriber has already flipped `suppressed` on, which would drop the very
    // chunk the flush existed to commit. This carries the exemption across that
    // gap. See flush() and handleWakeWord in useComprehendLive.js.
    this.pendingIgnoreSuppression = false;
    // Set only for the chunk straddling a suppression boundary — that one is
    // discarded so neither the explicit command nor the conversation tail
    // reaches the brain. Cleared by _startRecorder for every chunk after it.
    this.dropCurrent = false;
    this.window = '';
    this.lastSpeaker = null;
    this.sending = Promise.resolve();
    this.stopped = true;
    this.chunkSeq = 0;
    this.cutReason = 'hard';
    this.sessionStartedAt = 0;
    this.idleChunks = 0;
    // While true, keep listening (VAD/stream alive) but DON'T send chunks to
    // the brain — set during an explicit "Hey Comprehend"/chat turn so the
    // ambient brain doesn't double-act on the same words the realtime session
    // is already handling.
    this.suppressed = false;
  }

  setSuppressed(value) {
    const v = Boolean(value);
    if (v === this.suppressed) return;
    this.suppressed = v;
    // Discard whatever's mid-capture at the boundary so neither the explicit
    // command (entering) nor the conversation tail (leaving) leaks to the
    // brain — but do it by CYCLING THE RECORDER, not by clearing this.parts in
    // place. Only the first timeslice blob carries the container header
    // (EBML/ftyp); dropping it mid-recorder left every later blob a bare
    // cluster, so the next cut built a headerless chunk that looksLikeMediaContainer
    // rejected — real speech recorded, encoded, then dropped as 'corrupt-chunk'.
    // A full stop→start gives the next chunk its own header.
    this.dropCurrent = true;
    this._cut('suppress');
  }

  async start() {
    if (!this.stopped) return;
    this.stopped = false;
    this.sessionStartedAt = Date.now();
    this.idleChunks = 0;
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    if (this.stopped) {
      // stop() raced the permission prompt
      this.stream.getTracks().forEach((t) => t.stop());
      return;
    }

    // Light VAD: time-domain RMS off an analyser node.
    this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const source = this.audioCtx.createMediaStreamSource(this.stream);
    this.analyser = this.audioCtx.createAnalyser();
    this.analyser.fftSize = 2048;
    source.connect(this.analyser);

    this._startRecorder();
    this.vadTimer = setInterval(() => this._tick(), VAD_TICK_MS);
    this.onStatus('listening');
  }

  stop() {
    this.stopped = true;
    clearInterval(this.vadTimer);
    try {
      if (this.recorder && this.recorder.state !== 'inactive') {
        this.recorder.stop();
      }
    } catch (e) {
      // already stopped
    }
    this.recorder = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.audioCtx?.close().catch(() => {});
    this.audioCtx = null;
    this.onCatchup(false);
    this.onStatus('off');
    ambientInspector.reset();
  }

  /** Cut the accumulator NOW. forced=true tells the brain the clinician
   * explicitly asked it to act on everything (wake word / ⚡ Catch up).
   * ignoreSuppression keeps this one chunk alive through the suppression that
   * the same user gesture is about to switch on. */
  flush(forced = false, { noAct = false, ignoreSuppression = false } = {}) {
    if (this.stopped || !this.recorder) return;
    this.pendingForced = this.pendingForced || forced;
    this.pendingNoAct = this.pendingNoAct || noAct;
    this.pendingIgnoreSuppression =
      this.pendingIgnoreSuppression || ignoreSuppression;
    // Feedback starts at the tap, before the recorder even finishes stopping.
    if (forced) this.onCatchup(true);
    this._cut('flush');
  }

  _startRecorder() {
    this.parts = [];
    this.chunkSeq += 1;
    this.chunkStartedAt = Date.now();
    this.lastVoiceAt = Date.now();
    this.heardVoice = false;
    this.voicedMs = 0;
    this.cutReason = 'hard';
    this.dropCurrent = false;
    const seq = this.chunkSeq;
    // Resolved at the very end of this recorder's onstop so a cut can AWAIT the
    // stop fully settling before the next recorder is created — the same
    // ordering the proven classic recorder uses (Recorder.js: await stop()
    // then start()). Recreating the recorder inside onstop / in the same tick
    // raced the encoder teardown and produced headerless, undecodable chunks.
    this.stopComplete = new Promise((resolve) => {
      this._resolveStopComplete = resolve;
    });
    const recorder = new MediaRecorder(this.stream);
    this.recorder = recorder;
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.parts.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(this.parts, {
        type: recorder.mimeType || 'audio/webm',
      });
      const durationSec = Math.round((Date.now() - this.chunkStartedAt) / 1000);
      const voicedMs = this.voicedMs;
      const forced = this.pendingForced;
      const noAct = this.pendingNoAct;
      const reason = this.stopped ? 'stop' : this.cutReason;
      // `suppressed` covers every chunk cut during an agent turn; `dropCurrent`
      // covers the one straddling either boundary. A flush that explicitly
      // asked to outlive the suppression it triggered (the wake word) wins.
      const drop =
        (this.suppressed || this.dropCurrent) && !this.pendingIgnoreSuppression;
      this.pendingForced = false;
      this.pendingNoAct = false;
      this.pendingIgnoreSuppression = false;
      // NOTE: the next recorder is NOT started here. The cut path awaits
      // `stopComplete` (resolved at the end of this handler) and starts the
      // next recorder then, so the stop fully settles first. See _cut().
      // Dropped (an explicit chat/realtime turn is in flight, or this chunk
      // straddles the boundary into or out of one): the brain must not
      // double-act on what the agent is already handling. Don't count it as
      // idle — the room isn't quiet.
      if (drop) {
        ambientInspector.push('suppressed', {
          seq,
          durationSec,
          reason,
          voicedMs,
        });
        if (forced) this.onCatchup(false);
      } else if ((voicedMs >= MIN_VOICED_MS || forced) && blob.size > 0) {
        // Quiet chunks aren't worth a Whisper call — a blip isn't speech.
        // Forced flushes go regardless: the point is settling the window.
        this.idleChunks = 0;
        this._enqueueSend(blob, durationSec, forced, {
          seq,
          reason,
          voicedMs,
          noAct,
        });
      } else {
        this.idleChunks += 1;
        ambientInspector.push('skipped', {
          seq,
          durationSec,
          reason,
          voicedMs,
          cause: 'silence',
        });
        // A forced flush that captured no audio still has to release the
        // bolt's working state.
        if (forced) this.onCatchup(false);
        if (this.idleChunks >= MAX_IDLE_CHUNKS) this._autoStop('idle');
      }
      // Stop has fully settled — unblock any cut awaiting the restart.
      this._resolveStopComplete();
    };
    recorder.start(1000); // 1s timeslices, same as the classic recorder
  }

  async _cut(reason) {
    if (!this.recorder || this.recorder.state !== 'recording') return;
    this.cutReason = reason || this.cutReason;
    const done = this.stopComplete;
    try {
      this.recorder.stop(); // onstop builds + sends, then resolves `done`
    } catch (e) {
      return; // recorder torn down mid-cut
    }
    // Await the stop fully settling, THEN start the next recorder — same
    // ordering as Recorder.js (await stop() then start()). Prevents the
    // same-tick restart that produced headerless, undecodable chunks.
    await done;
    if (!this.stopped) this._startRecorder();
  }

  _tick() {
    if (this.stopped || !this.analyser) return;
    const buf = new Float32Array(this.analyser.fftSize);
    this.analyser.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i += 1) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length);
    const now = Date.now();
    if (rms >= RMS_SPEECH) {
      this.lastVoiceAt = now;
      this.heardVoice = true;
      this.voicedMs += VAD_TICK_MS;
    }
    ambientInspector.live = {
      seq: this.chunkSeq,
      startedAt: this.chunkStartedAt,
      lastVoiceAt: this.lastVoiceAt,
      rms,
      heardVoice: this.heardVoice,
    };
    const age = now - this.chunkStartedAt;
    const silence = now - this.lastVoiceAt;
    if (now - this.sessionStartedAt >= MAX_SESSION_MS) {
      this._autoStop('cap');
      return;
    }
    if (age >= HARD_CUT_MS) this._cut('hard');
    else if (age >= MIN_CHUNK_MS && silence >= SILENCE_MS) this._cut('vad');
    else if (
      this.heardVoice &&
      age >= UTTERANCE_MIN_MS &&
      silence >= UTTERANCE_SILENCE_MS
    ) {
      this._cut('utterance');
    }
  }

  /** Demo-cap trip: stop and tell the panel why (see TODO at the top). */
  _autoStop(reason) {
    if (this.stopped) return;
    ambientInspector.push('autostop', { reason });
    this.stop();
    // stop() clears the live snapshot; leave the paused marker for the
    // monitor so it can say why and how to resume.
    ambientInspector.live = { paused: reason };
    ambientInspector.emitter.emit('update');
    this.onStatus('paused', reason);
  }

  _enqueueSend(blob, durationSec, forced, meta) {
    // Strictly sequential: window/lastSpeaker thread through responses.
    this.sending = this.sending.then(() =>
      this._send(blob, durationSec, forced, meta).catch((e) => {
        console.error('Ambient chunk failed (words ride the next window):', e);
      }),
    );
  }

  async _send(blob, durationSec, forced, meta = {}) {
    const sentAt = Date.now();
    const windowBefore = this.window.length;
    // Headerless/corrupt chunk (the stop→restart race): no backend can decode
    // it, so don't waste a round-trip across three transcription tiers. Drop it
    // and release any forced-catchup state. Words aren't lost — the rolling
    // window re-presents anything unsettled on the next chunk.
    if (!(await looksLikeMediaContainer(blob))) {
      ambientInspector.push('skipped', {
        ...meta,
        durationSec,
        cause: 'corrupt-chunk',
      });
      if (forced) this.onCatchup(false);
      return;
    }
    const audio = await blobToDataURL(blob);
    const s = liveStore.getState();
    const recording = s.sessions.filter((x) => x.recording);
    // The classic 2-min recorder (web app MultiRecorder) is the system of
    // record when it's running — ambient must not double-commit. It still
    // listens and acts; it just stops being the transcriber.
    const liveConversationIds = s.classicRecorderActive
      ? []
      : recording.map((x) => String(x.conversationId));
    const liveIdSet = new Set(recording.map((x) => String(x.conversationId)));
    const roster = s.sessions
      .filter((x) => !liveIdSet.has(String(x.conversationId)))
      .slice(0, 25)
      .map((x) => ({
        conversationId: String(x.conversationId),
        patient: x.patientName || x.name,
        state: x.status || (x.isToday === false ? 'prior' : 'scheduled'),
      }));
    const queueSnapshot = s.queue
      .filter((q) => q.status === 'pending')
      .slice(0, 25)
      .map((q) => ({ id: q.id, patient: q.patientName, title: q.title }));

    let resp;
    try {
      resp = await this.apiClient.ambientChunk({
        audio,
        duration: durationSec,
        liveConversationIds,
        window: this.window,
        previousLastSpeaker: this.lastSpeaker,
        roster,
        queueSnapshot,
        forced,
        // Evidence for the server-side hallucination filter: how much
        // actual voice the mic heard, and what the last chunk said
        // (hallucinations loop; humans don't).
        voicedMs: meta.voicedMs || 0,
        previousNewText: this.lastNewText || '',
      });
      if (!resp || resp.error) {
        throw new Error(resp?.error || 'ambient chunk error');
      }
    } catch (e) {
      ambientInspector.push('chunk', {
        ...meta,
        durationSec,
        forced,
        latencyMs: Date.now() - sentAt,
        liveTargets: liveConversationIds.length,
        error: String(e?.message || e),
      });
      if (forced) this.onCatchup(false);
      throw e;
    }
    this.window = (resp.window || '').slice(-WINDOW_CAP);
    this.lastSpeaker = resp.lastSpeaker || this.lastSpeaker;
    if (resp.newText) this.lastNewText = resp.newText;
    ambientInspector.window = this.window;
    ambientInspector.lastSpeaker = this.lastSpeaker;
    ambientInspector.push('chunk', {
      ...meta,
      durationSec,
      forced,
      latencyMs: Date.now() - sentAt,
      liveTargets: liveConversationIds.length,
      newText: resp.newText || '',
      noise: resp.noise || '',
      committed: Object.entries(resp.committed || {}).map(([cid, text]) => ({
        conversationId: cid,
        text,
      })),
      gate: resp.gate || { act: false, instruction: '' },
      windowBefore,
      windowAfter: this.window.length,
      lastSpeaker: this.lastSpeaker,
      brainError: Boolean(resp.brainError),
    });
    const gate = resp.gate;
    // noAct (the "Hey Comprehend" flush): transcript was still committed above,
    // but DON'T execute the brain's action — the realtime agent owns this turn.
    if (gate && gate.act && gate.instruction && !meta.noAct) {
      try {
        this.onGate(gate.instruction);
      } catch (e) {
        console.error('Ambient gate handoff failed:', gate, e);
      }
    }
    if (forced) this.onCatchup(false);
  }
}

const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export default AmbientRuntime;
